import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Sparkles, Phone, Users, MessageSquare, Video, Globe2, Workflow, ShieldCheck, ArrowLeftRight } from 'lucide-react';
import PageHero from '../components/PageHero';
import CtaBanner from '../components/CtaBanner';
import FaqAccordion from '../components/FaqAccordion';
import Seo from '../components/Seo';
import { VOIP_PRICING_MONTHLY, VOIP_PRICING_ANNUAL } from '../data/mock';

/**
 * /pricing — single, consolidated pricing page.
 *
 * Previously pricing only lived inside /business-voip's Cloud Hosted PBX
 * section, which buried the most-asked question on the site. This page:
 *   1. Lifts the Cloud Hosted PBX plans up to the hero ("Hosted PBX seats")
 *   2. Lays out add-on telephony products (SIP, Teams Direct Routing,
 *      Numbers, Contact Centre, Video, CRM) as quick price cards so
 *      buyers can self-qualify before contacting sales.
 *   3. Ships a short FAQ + a clear "talk to sales" exit for anyone who
 *      needs a custom quote.
 *
 * All prices are pulled from the existing mock + match what /business-voip
 * advertises. Single source of truth lives in src/data/mock.js.
 */
const FAQ = [
  { q: 'Do prices include VAT?', a: 'All prices are quoted exclusive of UK VAT. VAT is added at the prevailing UK rate on UK invoices. International customers are zero-rated where applicable.' },
  { q: 'Is there a minimum contract?', a: 'No. VoIPinger runs on a rolling monthly contract by default. Annual billing gets you a discount but it is not required. Port out at any time with 30 days notice.' },
  { q: 'Are number rentals included?', a: 'Each Cloud Hosted PBX seat includes one UK geographic DID. Additional numbers, non-geographic ranges (0800, 03xx) and international DIDs are priced per number — see the Phone Numbers / DID product card below.' },
  { q: 'What about international call rates?', a: 'Per-minute rates depend on destination. UK landline is approximately 0.5p/min, UK mobile around 3p/min, EU landlines from 1p/min. Full rate sheet is sent with your Order Form.' },
  { q: 'Do you charge a setup fee?', a: 'No setup fee for standard deployments. Custom integrations, on-prem SBC interop and large-scale ports are scoped per-project — usually one-off, not recurring.' },
  { q: 'Can we get a custom quote?', a: 'Yes — anyone above 25 seats or with mixed product needs (Teams Direct Routing + Contact Centre + numbers) should ask for a custom quote. We usually come back within a working day.' }
];

// Add-on products. Pricing reflects what each dedicated page advertises;
// keeps the snapshot consistent across the site.
const PRODUCTS = [
  {
    href: '/microsoft-teams',
    badge: 'Teams Direct Routing',
    title: 'Microsoft Teams Phone — TeamMate',
    Icon: Users,
    from: 'From £8',
    unit: 'user/mo',
    bullets: [
      'Microsoft-certified Direct Routing SBC included',
      'UK + international number porting',
      'Compliance recording (optional)',
      '30–60% cheaper than Microsoft Calling Plans',
    ],
    cta: 'Run the TCO calculator',
    ctaHref: '/microsoft-calling-plans-vs-teammate',
  },
  {
    href: '/sip-trunk',
    badge: 'SIP Trunking',
    title: 'SIP Trunk Packages',
    Icon: Phone,
    from: 'From £5',
    unit: 'channel/mo',
    bullets: [
      'Unlimited channels available',
      'UK + International DIDs',
      'Carrier-grade Tier-1 routing',
      'Pay as you grow — burst capacity included',
    ],
  },
  {
    href: '/virtual-numbers',
    badge: 'Phone Numbers / DID',
    title: 'Numbers in 145+ countries',
    Icon: Globe2,
    from: 'From £1',
    unit: 'number/mo',
    bullets: [
      'UK geographic, 0800, 03xx',
      'International DIDs in 145+ countries',
      'Toll-free + UIFN',
      'Number porting at £30 single / £150 range',
    ],
  },
  {
    href: '/inbound-contact-centre',
    badge: 'Inbound contact centre',
    title: 'Inbound Contact Centre',
    Icon: Workflow,
    from: 'From £24',
    unit: 'agent/mo',
    bullets: [
      '5 queue routing models',
      'Multi-level IVR + skill-based routing',
      'Live wallboards (CW / AWT / AHT / ABN / CA / CV)',
      'AI Sentiment Analysis on recordings',
    ],
  },
  {
    href: '/outbound-contact-centre',
    badge: 'Outbound contact centre',
    title: 'Outbound Contact Centre',
    Icon: ArrowLeftRight,
    from: 'From £32',
    unit: 'agent/mo',
    bullets: [
      'Predictive / Power / Progressive / Preview dialer',
      'AMD with 90%+ accuracy',
      'PCI-DSS pause / resume',
      'Compliance recording + DNC scrubbing',
    ],
  },
  {
    href: '/video-conferencing',
    badge: 'Video & meetings',
    title: 'Video Conferencing',
    Icon: Video,
    from: 'Included',
    unit: 'with PBX',
    bullets: [
      'Conferences up to 50 video + 25 audio',
      'Webinars up to 1,000 attendees',
      'WebRTC — no install',
      'Recording + AI transcription',
    ],
  },
  {
    href: '/crm-integrations',
    badge: 'CRM telephony',
    title: 'CRM Integrations (NAVA)',
    Icon: MessageSquare,
    from: 'From £3',
    unit: 'user/mo',
    bullets: [
      'Click-to-dial across 200+ apps',
      'Contact popping + activity logging',
      'Salesforce / HubSpot / Zoho / MS Teams',
      'Presence + preferred device control',
    ],
  },
];

export default function Pricing() {
  const [billing, setBilling] = useState('annual');
  const plans = billing === 'annual' ? VOIP_PRICING_ANNUAL : VOIP_PRICING_MONTHLY;

  // Derive a small "starts at" headline for SEO + above-the-fold.
  // VOIP_PRICING_* prices ship as "£11" strings — strip non-digits.
  const startsAt = useMemo(() => {
    const lowest = plans.reduce((acc, p) => {
      const n = Number(String(p.price || '').replace(/[^\d.]/g, ''));
      return Number.isFinite(n) && n > 0 && n < acc ? n : acc;
    }, 999999);
    return Number.isFinite(lowest) && lowest < 999999 ? lowest : null;
  }, [plans]);

  return (
    <>
      <Seo
        title="Pricing — UK cloud VoIP, Teams Direct Routing, Numbers | VoIPinger"
        description={`Transparent UK pricing for cloud telephony${startsAt ? ` — from £${startsAt} per user per month` : ''}. Cloud Hosted PBX, Microsoft Teams Direct Routing, SIP trunks, contact centre and numbers in 145+ countries.`}
        path="/pricing"
        faq={FAQ}
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Pricing', path: '/pricing' }]}
      />

      <PageHero
        eyebrow="Pricing"
        title="Transparent UK pricing"
        subtitle="Rolling monthly contracts, no setup fees, port-out at will. Pick a Hosted PBX seat or scope a stack across Teams Direct Routing, contact centre and numbers."
        breadcrumb="Pricing"
      />

      {/* Hosted PBX plans */}
      <section className="bg-white">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <span className="eyebrow">Cloud Hosted PBX</span>
            <h2 className="section-title mt-4">Per-user seats. Annual or monthly.</h2>
            <p className="section-sub mt-3">
              Each seat includes HD voice, smart call routing, voicemail-to-email, video meetings,
              mobile + desktop softphones, and one UK geographic DID. Annual billing saves you
              roughly two months versus monthly.
            </p>

            <div className="mt-7 inline-flex bg-slate-100 p-1 rounded-full">
              {['annual', 'monthly'].map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setBilling(opt)}
                  className={`px-5 py-2 rounded-full text-sm font-bold capitalize transition-all ${
                    billing === opt
                      ? 'bg-white text-violet-700 shadow-md'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {opt === 'annual' ? 'Annual (save ~17%)' : 'Monthly'}
                </button>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
            {plans.map((plan) => {
              const isFeatured = !!plan.highlight;
              return (
                <div
                  key={`${plan.name}-${billing}`}
                  className={`relative rounded-3xl border p-6 flex flex-col ${
                    isFeatured
                      ? 'bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white border-transparent shadow-2xl shadow-violet-500/30'
                      : 'bg-white text-slate-900 border-slate-200'
                  }`}
                >
                  {isFeatured && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-amber-400 text-amber-950 text-[10px] font-extrabold uppercase tracking-wider shadow">
                      <Sparkles size={11} /> Most popular
                    </span>
                  )}
                  <p className={`text-xs uppercase tracking-widest font-bold ${isFeatured ? 'text-violet-100/90' : 'text-violet-600'}`}>
                    {plan.name}
                  </p>
                  <p className="mt-3 text-4xl font-extrabold">
                    {plan.price}
                    <span className={`text-sm font-semibold ml-1 ${isFeatured ? 'text-violet-100/80' : 'text-slate-500'}`}>
                      /user/mo
                    </span>
                  </p>
                  <p className={`text-xs mt-1 ${isFeatured ? 'text-violet-100/80' : 'text-slate-500'}`}>
                    {plan.feature || (billing === 'annual' ? 'Annual billing · exc. VAT' : 'Monthly billing · exc. VAT')}
                  </p>
                  <ul className={`mt-5 space-y-2.5 text-sm leading-relaxed flex-1 ${isFeatured ? 'text-violet-50' : 'text-slate-700'}`}>
                    {(plan.items || plan.features || []).map((f) => (
                      <li key={f} className="flex items-start gap-2">
                        <Check size={15} className={`mt-0.5 ${isFeatured ? 'text-fuchsia-200' : 'text-violet-600'}`} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    className={`mt-6 inline-flex items-center justify-center gap-1.5 w-full px-4 py-2.5 rounded-xl text-sm font-bold transition-shadow ${
                      isFeatured
                        ? 'bg-white text-violet-700 hover:shadow-lg'
                        : 'bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white hover:shadow-lg'
                    }`}
                  >
                    Start free trial <ArrowRight size={14} />
                  </Link>
                </div>
              );
            })}
          </div>

          <p className="text-xs text-slate-500 text-center mt-6">
            All seats include unlimited UK landline minutes and 2,000 inclusive UK mobile minutes per user/month.
            Beyond that — per-minute rates apply.
          </p>
        </div>
      </section>

      {/* Add-on product cards */}
      <section className="bg-slate-50 border-y border-slate-100">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="eyebrow">Mix &amp; match</span>
            <h2 className="section-title mt-4">Add on what your stack actually needs</h2>
            <p className="section-sub mt-3">
              Each product below is priced independently and can sit alongside (or instead of) the
              Hosted PBX seats above. Most customers pick 2&ndash;3 of these.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
            {PRODUCTS.map((p) => (
              <div
                key={p.href}
                className="rounded-2xl bg-white border border-slate-200 p-6 flex flex-col hover:border-violet-300 hover:shadow-md transition-all"
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center shadow-md shadow-violet-500/20">
                    <p.Icon size={20} />
                  </span>
                  <span className="text-[10px] uppercase tracking-widest font-bold text-violet-700 bg-violet-50 px-2 py-1 rounded-full">
                    {p.badge}
                  </span>
                </div>
                <h3 className="font-extrabold text-slate-900 text-lg mb-2">{p.title}</h3>
                <p className="text-2xl font-extrabold gradient-text">
                  {p.from}
                  <span className="text-sm font-semibold text-slate-500 ml-1">/{p.unit}</span>
                </p>
                <ul className="mt-4 space-y-2 text-sm text-slate-600 flex-1">
                  {p.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <Check size={14} className="text-violet-600 mt-0.5 flex-shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-5 flex flex-wrap items-center gap-2">
                  <Link to={p.href} className="inline-flex items-center gap-1.5 text-sm font-bold text-violet-700 hover:gap-2 transition-all">
                    Read more <ArrowRight size={14} />
                  </Link>
                  {p.cta && p.ctaHref && (
                    <Link to={p.ctaHref} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-violet-50 text-violet-700 text-xs font-bold hover:bg-violet-100 transition-colors">
                      {p.cta}
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Includes everywhere */}
      <section className="bg-white">
        <div className="container-x section-pad">
          <div className="max-w-4xl mx-auto rounded-3xl bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 border border-violet-100 p-8 md:p-10">
            <div className="flex items-start gap-4">
              <span className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white flex items-center justify-center flex-shrink-0 shadow-lg shadow-violet-500/30">
                <ShieldCheck size={22} />
              </span>
              <div>
                <h2 className="text-xl font-extrabold text-slate-900 mb-2">Included with every plan</h2>
                <ul className="grid sm:grid-cols-2 gap-2 text-sm text-slate-700">
                  {[
                    '99.99% uptime SLA across three Tier-4 datacentres',
                    'UK-based support, Mon–Fri 09:00–18:00 + after-hours NOC',
                    'Rolling monthly contract — no minimum term',
                    'Free porting in (£30 single, £150 range out)',
                    'GDPR + PCI-DSS aligned recording',
                    'Open API access (240+ endpoints)',
                  ].map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check size={14} className="text-violet-600 mt-1" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FaqAccordion items={FAQ} title="Pricing FAQ" />
      <CtaBanner
        eyebrow="Talk to sales"
        title="Need a custom quote?"
        subtitle="Above 25 seats, mixed product needs, or unusual carrier scenarios — we'll model your stack and come back within a working day."
        primary="Get a custom quote"
        primaryHref="/contact?subject=Custom+pricing+quote"
      />
    </>
  );
}
